'use client';

import { useEffect, useState } from 'react';

import { TextField as TextFieldMUI } from '@mui/material';

import { mapInputModeFromType } from './textFieldUtil';

import type { TNameType } from '../../types/components/components.type';
import type { ITextFieldProps, TTextFieldValue } from './textField.type';

export const TextField = <TName extends TNameType>({
  name,
  value,
  label,
  handleChange,
  handleBlur,
  type = 'text',
  autoFocus = false,
  disabled = false,
  multiline = false,
  size = 'medium',
  color = 'primary',
  className = '',
  errorMessage = '',
  helperText,
  maxRows = 1,
  minRows = 1,
  changeMode = 'blur',
  variant = 'outlined',
  sx = {},
  autoComplete,
  startAdornment,
  endAdornment,
  disableUnderline = false,
  autocapitalize = true,
  autocorrect = true,
}: ITextFieldProps<TName>) => {
  const [localValue, setLocalValue] = useState<TTextFieldValue>(value);

  useEffect(() => {
    if (localValue !== value) {
      setLocalValue(value);
    }
  }, [value]);

  const isError = !!errorMessage;

  return (
    <TextFieldMUI
      {...(autoComplete && { autoComplete })}
      sx={sx}
      variant={variant}
      className={className}
      fullWidth
      name={name}
      label={label}
      value={localValue}
      // we want input to be text for number
      type={type === 'number' ? 'text' : type}
      onChange={({ target: { value: targetValue } }) => {
        setLocalValue(targetValue);

        if (changeMode === 'change' || handleBlur) {
          handleChange({ key: name, value: targetValue });
        }
      }}
      onBlur={(event) => {
        if (changeMode === 'blur' && !handleBlur) {
          handleChange({ key: name, value: localValue });
        }

        if (handleBlur) {
          handleBlur(event);
        }
      }}
      autoFocus={autoFocus}
      disabled={disabled}
      multiline={multiline}
      size={size}
      color={color}
      maxRows={maxRows}
      minRows={minRows}
      error={errorMessage !== ''}
      helperText={isError ? errorMessage : helperText}
      slotProps={{
        input: {
          startAdornment,
          endAdornment,
          inputProps: {
            ...(autocapitalize ? {} : { autocapitalize: 'none' }),
            ...(autocorrect ? {} : { autocorrect: 'off' }),
            role: 'textbox',
            inputMode: mapInputModeFromType(type),
          },
          disableUnderline,
        },
      }}
    />
  );
};
